import React from 'react';

/**
 * Shared Pagination component.
 *
 * Props:
 *   currentPage  — 1-based current page number
 *   totalPages   — total number of pages
 *   onPageChange — callback(pageNumber)
 */
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  if (!totalPages || totalPages <= 1) return null;

  const MAX_VISIBLE = 5;

  const getPageNumbers = () => {
    if (totalPages <= MAX_VISIBLE) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    let start = Math.max(1, currentPage - Math.floor(MAX_VISIBLE / 2));
    let end = start + MAX_VISIBLE - 1;

    if (end > totalPages) {
      end = totalPages;
      start = Math.max(1, end - MAX_VISIBLE + 1);
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const pages = getPageNumbers();
  const showLeadingEllipsis = pages[0] > 2;
  const showTrailingEllipsis = pages[pages.length - 1] < totalPages - 1;
  const showFirstPage = pages[0] > 1;
  const showLastPage = pages[pages.length - 1] < totalPages;

  return (
    <div className="data-pagination" role="navigation" aria-label="Pagination">
      {/* Previous */}
      <button
        className="data-page-btn data-page-nav"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        aria-label="Previous page"
      >
        <i className="bi bi-chevron-left" />
      </button>

      {/* First page */}
      {showFirstPage && (
        <>
          <button
            className="data-page-btn"
            onClick={() => onPageChange(1)}
          >
            1
          </button>
          {showLeadingEllipsis && (
            <span className="data-page-ellipsis">…</span>
          )}
        </>
      )}

      {/* Page numbers */}
      {pages.map((page) => (
        <button
          key={page}
          className={`data-page-btn${page === currentPage ? ' active' : ''}`}
          onClick={() => onPageChange(page)}
          aria-current={page === currentPage ? 'page' : undefined}
        >
          {page}
        </button>
      ))}

      {/* Last page */}
      {showLastPage && (
        <>
          {showTrailingEllipsis && (
            <span className="data-page-ellipsis">…</span>
          )}
          <button
            className="data-page-btn"
            onClick={() => onPageChange(totalPages)}
          >
            {totalPages}
          </button>
        </>
      )}

      {/* Next */}
      <button
        className="data-page-btn data-page-nav"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        aria-label="Next page"
      >
        <i className="bi bi-chevron-right" />
      </button>
    </div>
  );
};

export default Pagination;
